import React, { MouseEventHandler, TouchEventHandler, useEffect, useRef, useState } from 'react';
import { Box, Table, TableBody, TableCell, TableContainer, TableRow } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { PitConfType } from 'types/machineConf/machineConfType';
import { to2DArrayPitJack, toPitConfFrom2DArray } from 'utils/machineConf.helper';
import CLSFCN from 'constants/classification';
import pitMarkIcon from 'assets/images/pitMarkIcon.svg';
import pitCrossIcon from 'assets/images/pitCrossIcon.svg';
import { useAppSelector } from 'redux/hooks';
import COMMON from 'constants/common';

type Props = {
  pitConf: PitConfType;
  onChange: (newPitConf: PitConfType) => void;
  className?: string;
};

const useStyles = makeStyles()((theme) => ({
  root: {
    userSelect: 'none',
    touchAction: 'none',
    '& *': {
      touchAction: 'none',
    },
  },
  container: {
    width: '100%',
    height: '100%',
  },
  table: {
    width: '100%',
    height: '100%',
    borderCollapse: 'collapse',
    position: 'relative',
    '& tr:nth-child(1)': {
      '& img': {
        width: 60,
        height: 60,
      },
    },
  },
  table9: {
    '& tr:nth-child(1)': {
      height: 79,
    },

    '& tr:nth-child(1) td': {
      height: 79,
    },
    '& tr:not(:first-child)': {
      height: 129,
      '& img': {
        width: 114,
        height: 114,
      },
    },
    '& tr:nth-child(even)': {
      borderTop: `2px solid ${theme.base.tableBorder}`,
      borderBottom: `2px solid ${theme.base.tableBorder}`,
    },
    '& tr:nth-child(odd)': {
      borderTop: `2px solid ${theme.base.tableBorder}`,
      borderBottom: `2px solid ${theme.base.tableBorder}`,
    },
  },
  table30: {
    '& tr:nth-child(1)': {
      height: '16.67%',
    },

    '& tr:nth-child(1) td': {
      height: '16.67%',
    },
    '& tr:not(:first-child)': {
      height: '16.67%',
      '& img': {
        width: '',
        height: '',
      },
    },
    '& tr:nth-child(even)': {
      borderTop: `2px solid ${theme.base.tableBorder}`,
      borderBottom: `2px dashed ${theme.base.tableBorder}`,
    },
    '& tr:nth-child(odd)': {
      borderTop: `2px dashed ${theme.base.tableBorder}`,
      borderBottom: `2px solid ${theme.base.tableBorder}`,
    },
  },
  tableRow: {
    height: '33.34%',
    '& td:nth-child(odd)': {
      borderLeft: `2px solid ${theme.base.tableBorder}`,
      borderRight: `2px solid ${theme.base.tableBorder}`,
    },
    '& td:nth-child(even)': {
      borderLeft: `2px solid ${theme.base.tableBorder}`,
      borderRight: `2px solid ${theme.base.tableBorder}`,
    },
  },
  tableRow2: {
    height: '16.67%',
    '& td:nth-child(odd)': {
      borderLeft: `2px solid ${theme.base.tableBorder}`,
      borderRight: `0px dashed ${theme.base.tableBorder}`,
    },
    '& td:nth-child(even)': {
      borderLeft: `2px dashed ${theme.base.tableBorder}`,
      borderRight: `2px solid ${theme.base.tableBorder}`,
    },
  },

  tableCell: {
    padding: '0',
    width: '3.34%',
  },
  tableCell2: {
    padding: '0',
    width: '16.67%',
  },
  pitBlock: {
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  pitBlock9: {
    padding: '0',
  },
  pitBlock30: {
    padding: '8px',
  },
  pitIcon: {
    pointerEvents: 'none',
  },
  disable: {
    position: 'absolute',
    background: ' rgba(000,000,000, .4)',
  },
  disableColumns: {
    width: 65,
    height: '100%',
    top: 0,
  },
  disableColumnsEnd: {
    width: 65,
    height: '100%',
    top: 0,
    right: 0,
  },
  disableRows: {
    width: '100%',
    height: 78,
    top: 0,
  },
  disableRowsEnd: {
    width: '100%',
    height: 65,
    bottom: 0,
  },
}));

// TouchMove中はこのコンポーネント内部のステートを切り替えるのみとする
// TouchEnd時にonChangeを介して渡された基のステートを更新する

const PitPanelJack: React.FC<React.PropsWithChildren<Props>> = (props) => {
  const { pitConf, onChange, className } = props;
  const { classes } = useStyles();
  const conf = useAppSelector((state) => state.machineConf.machineConf.conf);
  const [pits, setPits] = useState(to2DArrayPitJack(pitConf));
  const inputMode = useRef<'0' | '1' | null>(null);
  const mouseEventSkip = useRef<boolean>(false);
  useEffect(() => {
    setPits(to2DArrayPitJack(pitConf));
  }, [pitConf.settingPattern]);

  const handleClick: MouseEventHandler<HTMLDivElement> = (event) => {
    if (mouseEventSkip.current) return;
    inputMode.current = null;
    updatePanel(event.clientX, event.clientY);
  };

  const handleTouchStart: TouchEventHandler<HTMLDivElement> = (event) => {
    mouseEventSkip.current = true;
    // ※TouchMoveイベントは指を離すまで一番最初にタッチしたコンポーネントに対して発生し続ける
    inputMode.current = null;
    handleTouchMove(event);
  };

  const handleTouchMove: TouchEventHandler<HTMLDivElement> = (event) => {
    // ※TouchMoveイベントは指を離すまで一番最初にタッチしたコンポーネントに対して発生し続ける
    const touch = event.changedTouches[0];
    updatePanel(touch.clientX, touch.clientY);
  };

  const handleTouchEnd = () => {
    // ※PCとモバイルでイベントの発生仕方が異なる
    //   PC：マウスダウンした要素に関係なく、単純にマウスアップした要素でイベントが発生する→MouseUpだけでは編集終了を捕捉できないのでMouseLeaveも追加
    //   モバイル：要素内でタッチ開始すれば要素外でタッチ終了してもタッチ開始した要素でTouchEndイベントが発生する
    inputMode.current = null;
    if (pitConf) {
      const temp = toPitConfFrom2DArray(pitConf, pits);
      onChange(temp);
    }
  };

  const isDisableColumns =
    pitConf.settingPattern === CLSFCN.PIT_CONFIG_JACK.PATTERN.DETAIL &&
    Number(conf?.leftArmSize) === Number(CLSFCN.ARM_SIZE.LARGE) &&
    Number(conf?.catcherDirection) !== Number(COMMON.CATCHER_DIRECTION.NORMAL);

  const isDisableRows =
    pitConf.settingPattern === CLSFCN.PIT_CONFIG_JACK.PATTERN.DETAIL &&
    Number(conf?.leftArmSize) === Number(CLSFCN.ARM_SIZE.LARGE) &&
    Number(conf?.catcherDirection) === Number(COMMON.CATCHER_DIRECTION.NORMAL);

  const disableRows = (dataset: { x?: string; y?: string }) => {
    const minDatasetX = 0;
    const maxDatasetX = 5;
    const minDatasetY = 1;
    const maxDatasetY = 5;

    let isDisable = false;
    if (isDisableColumns && (Number(dataset.y) === minDatasetY || Number(dataset.y) === maxDatasetY)) {
      isDisable = true;
    }

    if (isDisableRows && (Number(dataset.x) === minDatasetX || Number(dataset.x) === maxDatasetX)) {
      isDisable = true;
    }

    return isDisable;
  };

  const updatePanel = (clientX: number, clientY: number) => {
    // タッチしている落とし穴を取得
    const element = document.elementFromPoint(clientX, clientY) as HTMLDivElement;
    const isDisable = disableRows({ x: element.dataset.x, y: element.dataset.y });
    if (isDisable) return;
    if (element && element.dataset.value) {
      // 最初にタッチした落とし穴の値を反転した値を入力モードとして設定
      if (inputMode.current == null) {
        inputMode.current = element.dataset.value === '1' ? '0' : '1';
      }

      // 現在の入力モードとタッチしている落とし穴の値が違ったら落とし穴の値を変更
      const x = Number(element.dataset.x);
      const y = Number(element.dataset.y);
      if (inputMode.current !== pits[y][x]) {
        const newone = pits.map((row) => row.slice());
        newone[y][x] = inputMode.current;
        setPits(newone);
      }
    }
  };

  return (
    <Box
      className={`${classes.root} ${className}`}
      onMouseDown={handleClick}
      onMouseLeave={handleTouchEnd}
      onMouseUp={handleTouchEnd}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}>
      <TableContainer className={classes.container}>
        <Table
          className={`${classes.table} ${
            pitConf.settingPattern === CLSFCN.PIT_CONFIG.PATTERN.DETAIL ? classes.table30 : classes.table9
          }`}>
          <TableBody>
            {pits.map((row, i) => {
              if (i === 0 && pitConf.settingPattern === CLSFCN.PIT_CONFIG.PATTERN.DETAIL) return <></>;
              return (
                <TableRow
                  className={`${
                    pitConf.settingPattern === CLSFCN.PIT_CONFIG.PATTERN.DETAIL ? classes.tableRow2 : classes.tableRow
                  }`}
                  // eslint-disable-next-line react/no-array-index-key
                  key={i}>
                  {row.map((value, j) => (
                    <TableCell
                      className={`${
                        pitConf.settingPattern === CLSFCN.PIT_CONFIG.PATTERN.DETAIL
                          ? classes.tableCell2
                          : classes.tableCell
                      } `}
                      // eslint-disable-next-line react/no-array-index-key
                      key={j}>
                      <Box
                        data-x={j}
                        data-y={i}
                        data-value={value}
                        className={`${classes.pitBlock} ${
                          pitConf.settingPattern === CLSFCN.PIT_CONFIG.PATTERN.DETAIL
                            ? classes.pitBlock30
                            : classes.pitBlock9
                        }`}>
                        {value === '1' && <img src={pitMarkIcon} alt="○" className={classes.pitIcon} />}
                        {value !== '1' && <img src={pitCrossIcon} alt="×" className={classes.pitIcon} />}
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>

          {isDisableRows && (
            <>
              <div className={`${classes.disable} ${classes.disableColumns}`} />
              <div className={`${classes.disable} ${classes.disableColumnsEnd}`} />
            </>
          )}

          {isDisableColumns && (
            <>
              <div className={`${classes.disable} ${classes.disableRows}`} />
              <div className={`${classes.disable} ${classes.disableRowsEnd}`} />
            </>
          )}
        </Table>
      </TableContainer>
    </Box>
  );
};

export default PitPanelJack;
